<template>
  <div class="quota-warning-page">
    <div class="page-header">
      <h2 class="page-title">配额预警</h2>
    </div>

    <filter-bar>
      <div class="filter-item">
        <span class="filter-label">时间周期</span>
        <el-radio-group v-model="periodType" size="small">
          <el-radio-button label="month">月</el-radio-button>
          <el-radio-button label="year">年</el-radio-button>
        </el-radio-group>
      </div>
      <div class="filter-item">
        <span class="filter-label">时间段</span>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          size="small"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
        />
      </div>
    </filter-bar>

    <chart-card title="配额预警趋势" class="chart-section">
      <base-chart :option="chartOption" />
    </chart-card>

    <div class="table-section">
      <el-table :data="tableRows" border stripe size="small">
        <el-table-column prop="label" label="序号" width="160" fixed />
        <el-table-column
          v-for="col in tableColumns"
          :key="col.prop"
          :prop="col.prop"
          :label="col.label"
          min-width="120"
          align="center"
        >
          <template slot-scope="{ row }">
            <span
              v-if="row.type === 'diff' && row[col.prop] !== '' && row[col.prop] < 0"
              class="text-danger"
            >
              {{ formatCell(row[col.prop]) }}
            </span>
            <span v-else>{{ formatCell(row[col.prop]) }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import FilterBar from '@/components/common/FilterBar.vue'
import ChartCard from '@/components/common/ChartCard.vue'
import BaseChart from '@/components/charts/BaseChart.vue'
import { getQuotaWarningData } from '@/api/modules/carbonAsset'
import { formatNumber } from '@/utils/format'

export default {
  name: 'QuotaWarning',
  components: { FilterBar, ChartCard, BaseChart },
  data() {
    return {
      periodType: 'month',
      dateRange: ['2025-01-01', '2025-12-31'],
      quotaData: [],
      forecastStartIndex: 5,
      chartOption: {}
    }
  },
  computed: {
    tableColumns() {
      return this.quotaData.map((item, index) => ({
        prop: `m${index}`,
        label: item.month.replace('2025-', '2025-')
      }))
    },
    tableRows() {
      if (!this.quotaData.length) return []
      const predicted = { label: '预测配额 (tCO2)', type: 'predicted' }
      const actual = { label: '排放量 (tCO2)', type: 'actual' }
      const diff = { label: '盈缺值 (tCO2)', type: 'diff' }
      this.quotaData.forEach((item, index) => {
        predicted[`m${index}`] = item.predicted
        actual[`m${index}`] = item.actual
        diff[`m${index}`] = item.diff
      })
      return [predicted, actual, diff]
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      const res = await getQuotaWarningData({ periodType: this.periodType })
      this.quotaData = res.data.quotaWarning
      this.forecastStartIndex = res.data.forecastStartIndex
      this.buildChartOption()
    },
    formatCell(val) {
      if (val === null || val === undefined || val === '') return ''
      return formatNumber(val, 0)
    },
    buildChartOption() {
      const months = this.quotaData.map(item => item.month)
      const predicted = this.quotaData.map(item => item.predicted)
      const actual = this.quotaData.map(item => item.actual)
      const splitIndex = this.forecastStartIndex

      const predictedHistorical = predicted.map((v, i) => (i < splitIndex ? v : null))
      const predictedForecast = predicted.map((v, i) => (i >= splitIndex - 1 ? v : null))

      this.chartOption = {
        tooltip: { trigger: 'axis' },
        legend: {
          data: ['预测配额 (tCO2)', '排放量 (tCO2)'],
          bottom: 0
        },
        grid: { left: 60, right: 30, top: 30, bottom: 50 },
        xAxis: {
          type: 'category',
          data: months,
          boundaryGap: false
        },
        yAxis: {
          type: 'value',
          name: 'tCO2',
          axisLabel: {
            formatter: val => (val / 10000) + '万'
          }
        },
        series: [
          {
            name: '预测配额 (tCO2)',
            type: 'line',
            data: predictedHistorical,
            smooth: true,
            symbol: 'circle',
            lineStyle: { color: '#409EFF', width: 2 },
            itemStyle: { color: '#409EFF' },
            areaStyle: {
              color: 'rgba(103, 194, 58, 0.15)',
              origin: 'auto'
            },
            stack: 'area'
          },
          {
            name: '预测配额 (tCO2)',
            type: 'line',
            data: predictedForecast,
            smooth: true,
            symbol: 'circle',
            lineStyle: { color: '#409EFF', width: 2, type: 'dashed' },
            itemStyle: { color: '#409EFF' },
            showSymbol: true
          },
          {
            name: '排放量 (tCO2)',
            type: 'line',
            data: actual,
            smooth: true,
            symbol: 'circle',
            lineStyle: { color: '#E6A23C', width: 2 },
            itemStyle: { color: '#E6A23C' },
            areaStyle: {
              color: 'rgba(245, 108, 108, 0.2)'
            }
          }
        ]
      }
    }
  }
}
</script>

<style lang="less" scoped>
.chart-section {
  height: 420px;
  margin-bottom: 16px;
}

.table-section {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
  overflow-x: auto;
}
</style>
